import { useState } from "react";
import { Check, X } from "lucide-react";

export interface QuestionData {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

interface QuizCardProps {
  card: QuestionData;
  value: number | null;
  onAnswer?: (index: number) => void;
}

export const QuizCard: React.FC<QuizCardProps> = ({
  card,
  value,
  onAnswer,
}) => {
  const selectedOption = value;
  const isSubmitted = value !== null;

  const isCorrect = selectedOption === card.correctAnswer;

  const handleOptionClick = (index: number) => {
    onAnswer(index);
  };

  return (
    <div className="bg-theme-0 px-6 py-6 border border-theme rounded-xl w-[576px] shadow-md">
      <div className="flex flex-col gap-4">
        <div className="text-2xl text-theme-0 font-sans font-medium leading-9">
          {card.question}
        </div>
        <div className="flex flex-col gap-2">
          {card.options.map((option, index) => {
            const isSelected = selectedOption === index;
            const isCorrectAnswer = index === card.correctAnswer;
            const showBorder = isSubmitted && (isCorrectAnswer || isSelected);
            const borderColor = isCorrectAnswer
              ? "border-green-500"
              : "border-red-500";
            const shouldDim = isSubmitted && !isCorrectAnswer && !isSelected;

            return (
              <div
                key={index}
                onClick={() => handleOptionClick(index)}
                className={`
                                    p-3 rounded-lg border-2 bg-theme-1 transition-all
                                    ${
                                      showBorder
                                        ? borderColor
                                        : "border-transparent"
                                    }
                                    ${
                                      !isSubmitted &&
                                      "hover:brightness-95 cursor-pointer"
                                    }
                                    ${shouldDim && "opacity-20"}
                                `}
              >
                <div className="text-theme-0 font-sans text-base flex gap-2">
                  <span className="font-semibold">
                    {String.fromCharCode(65 + index)}.
                  </span>
                  <span>{option}</span>
                </div>
              </div>
            );
          })}
        </div>
        <div
          className={`overflow-hidden transition-all duration-1500 ${
            isSubmitted ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div
            className={`p-3 rounded-lg ${
              isCorrect
                ? "bg-green-200 text-green-600"
                : "bg-red-200 text-red-600"
            }`}
          >
            <div className="flex items-center gap-1 py-1">
              {isCorrect ? <Check className="h-5" /> : <X className="h-5" />}
              {isCorrect ? (
                <span className="font-semibold">Correct</span>
              ) : (
                <span className="font-semibold">Incorrect</span>
              )}
            </div>
            <div className="font-sans">{card.explanation}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
